import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { StackedBarChart } from '../../../charts/BarChart'
import { format } from 'date-fns'
import _ from 'lodash'
import { formatNumber } from '../../../../helpers/UtilityHelper'
import getColor from '../../../charts/colors'

const PincodeMoMReport = ({ allRtoData = [] }) => {
    const [view, setView] = useState('table')

    const { months, topPincodes, matrix, grandTotals } = useMemo(() => {
        if (!allRtoData || allRtoData.length === 0) return { months: [], topPincodes: [], matrix: {}, grandTotals: {} }

        const grouped = _.groupBy(allRtoData, item => {
            try {
                return format(new Date(item.awbRegisteredDate), 'MMM yyyy')
            } catch { return 'Unknown' }
        })

        const months = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b))
        const pincodeCount = _.countBy(allRtoData, 'dropPincode')
        const topPincodes = Object.entries(pincodeCount)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 15)
            .map(([pin]) => pin)

        const matrix = {}
        const grandTotals = {}
        topPincodes.forEach(pin => {
            matrix[pin] = {}
            let pinTotal = 0
            months.forEach(m => {
                const count = (grouped[m] || []).filter(i => i.dropPincode === pin).length
                matrix[pin][m] = count
                pinTotal += count
            })
            grandTotals[pin] = pinTotal
        })

        return { months, topPincodes, matrix, grandTotals }
    }, [allRtoData])

    if (topPincodes.length === 0) return null

    const chartData = months.map(m => {
        const row = { name: m }
        topPincodes.slice(0, 8).forEach(pin => {
            row[pin] = matrix[pin]?.[m] || 0
        })
        return row
    })

    const chartOptions = {
        xaxis: 'name',
        data: topPincodes.slice(0, 8).map((pin, i) => ({
            dataKey: pin, label: pin, fill: getColor(i),
        })),
        stackId: 'a',
        showLabels: false,
        legendAlign: 'left',
    }

    return (
        <Card elevation={1}>
            <CardHeader
                title="Pincode Month-on-Month RTO Bifurcation"
                subheader="Top 15 pincodes with highest RTO counts, shown across months"
                action={
                    <ToggleButtonGroup value={view} exclusive size="small" onChange={(_e, v) => v && setView(v)}>
                        <ToggleButton value="chart">Chart</ToggleButton>
                        <ToggleButton value="table">Table</ToggleButton>
                    </ToggleButtonGroup>
                }
            />
            <CardContent>
                {view === 'chart' ? (
                    <StackedBarChart data={chartData} chartOptions={chartOptions} />
                ) : (
                    <TableContainer sx={{ maxHeight: 500 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', bgcolor: '#FEF3C7', position: 'sticky', left: 0, zIndex: 3 }}>Pincode</TableCell>
                                    {months.map(m => (
                                        <TableCell key={m} align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', bgcolor: '#FEF3C7' }}>{m}</TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', bgcolor: '#FEF3C7' }}>Grand Total</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {topPincodes.map(pin => (
                                    <TableRow key={pin} hover>
                                        <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1 }}>{pin}</TableCell>
                                        {months.map(m => (
                                            <TableCell key={m} align="center" sx={{ fontSize: '0.75rem' }}>{formatNumber(matrix[pin]?.[m] || 0)}</TableCell>
                                        ))}
                                        <TableCell align="center" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{formatNumber(grandTotals[pin])}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#1F2937' }}>
                                    <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem', position: 'sticky', left: 0, bgcolor: '#1F2937', zIndex: 1 }}>Grand Total</TableCell>
                                    {months.map(m => (
                                        <TableCell key={m} align="center" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                            {formatNumber(topPincodes.reduce((sum, pin) => sum + (matrix[pin]?.[m] || 0), 0))}
                                        </TableCell>
                                    ))}
                                    <TableCell align="center" sx={{ color: '#fff', fontWeight: 'bold', fontSize: '0.75rem' }}>
                                        {formatNumber(Object.values(grandTotals).reduce((a, b) => a + b, 0))}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    )
}

export default PincodeMoMReport
